/* "Hitta-rutin" — ADHD-friendly guided search wizard.
 * Step-by-step questions that calm panic + suggest where to look. */
import React, { useState } from 'react';
import { X, ArrowRight, RotateCcw, Sparkles } from 'lucide-react';

const STEPS = [
  {
    q: 'Andas djupt först 🌸 — när hade du den senast?',
    options: [
      { label: 'Imorse', hint: 'Kolla nattduksbord, kläder du hade på dig, badrummet' },
      { label: 'Igår kväll', hint: 'Säng, soffan, jackfickor, väskan från igår' },
      { label: 'För några dagar sedan', hint: 'Förra veckans väska, jacka i hallen, kontoret' },
      { label: 'Jag har ingen aning', hint: 'Det är ok — vi tar det steg för steg 💖' },
    ],
  },
  {
    q: 'Var var du senast?',
    options: [
      { label: '🛏️ Hemma — i sovrummet', hint: 'Kolla under kuddar, sängbord, kläder på golvet' },
      { label: '🛋️ Hemma — vardagsrum/soffa', hint: 'Mellan kuddar, under filtar, soffbordet' },
      { label: '🍽️ Hemma — kök', hint: 'Bordet, bänken, kylskåp (på riktigt), brödkorgen' },
      { label: '🚿 Hemma — badrum', hint: 'Tvättkorgen, fickan på morgonrocken, hyllan' },
      { label: '🚪 Hemma — hall/entré', hint: 'Jackfickor, väska, skohylla, nyckelskål' },
      { label: '🚗 Bil / kollektivtrafik', hint: 'Stolar, hatthylla, fickor, handsfacket' },
      { label: '💼 Jobb / skola', hint: 'Skrivbordet, väskan, jackan på stolen' },
    ],
  },
  {
    q: 'Har du kollat alla fickor på alla kläder från idag/igår?',
    options: [
      { label: 'Ja, alla', hint: 'Bra! Hoppa till nästa steg' },
      { label: 'Inte alla — jag kollar nu', hint: 'Inkl. byxor, jackor, hoodies, skjorta' },
    ],
  },
  {
    q: 'Sist försök — har du kollat dessa platser där vi oftast tappar grejer?',
    options: [
      { label: '✓ Sängen / under sängen', hint: 'Klassiker — kolla mellan dyna och madrass' },
      { label: '✓ I "fel" rum', hint: 'Köksbord, vid kaffemaskinen, badrum' },
      { label: '✓ I väska/ryggsäck — alla fack', hint: 'Även det lilla insidefacket' },
      { label: '✓ Laddningsplatser', hint: 'Vid sängen, skrivbordet, soffan' },
      { label: '✓ Hörlurar i öronen redan?', hint: '😅 Kolla på riktigt — det händer oftare än man tror' },
    ],
  },
];

export default function SearchRoutineModal({ onClose, itemName }) {
  const [step, setStep] = useState(0);
  const [selected, setSelected] = useState(null);
  const cur = STEPS[step];
  const done = step >= STEPS.length;

  const select = (opt) => {
    setSelected(opt);
  };

  const next = () => {
    setSelected(null);
    setStep((s) => s + 1);
  };

  const restart = () => {
    setStep(0);
    setSelected(null);
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm flex items-center justify-center p-3"
         onClick={onClose} data-testid="search-routine-modal">
      <div onClick={(e) => e.stopPropagation()}
           className="neo-card bg-gradient-to-br from-[#FFF8FB] to-[#FFE8F5] w-full max-w-md p-4 space-y-3 max-h-[92vh] overflow-y-auto">
        <header className="flex items-center justify-between">
          <h3 className="font-['Fredoka'] font-bold text-lg flex items-center gap-1">
            🔍 Hitta-rutin {itemName ? `— ${itemName}` : ''}
          </h3>
          <button onClick={onClose} data-testid="search-routine-close"><X className="w-5 h-5" /></button>
        </header>

        {/* Progress dots */}
        <div className="flex gap-1 justify-center">
          {STEPS.map((_, i) => (
            <span key={i}
              className={`w-2 h-2 rounded-full transition ${
                i < step ? 'bg-[#D06892]' :
                i === step ? 'bg-[#D06892] scale-150' : 'bg-black/15'
              }`}
            />
          ))}
        </div>

        {!done ? (
          <>
            <div className="neo-card p-3 bg-white">
              <p className="font-bold text-sm mb-2" data-testid="routine-question">
                {step + 1}. {cur.q}
              </p>
              <div className="space-y-1.5">
                {cur.options.map((opt, i) => (
                  <button
                    key={i}
                    onClick={() => select(opt)}
                    className={`w-full text-left text-xs p-2 rounded-lg border-2 transition ${
                      selected === opt
                        ? 'bg-[#FFE8F5] border-[#D06892] ring-2 ring-[#D06892]/30'
                        : 'bg-white border-black/10 hover:border-[#D06892]/40'
                    }`}
                    data-testid={`routine-opt-${step}-${i}`}
                  >
                    <span className="font-bold">{opt.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {selected && (
              <div className="neo-card p-3 bg-[#FFFAF0] text-xs space-y-2" data-testid="routine-hint">
                <p className="font-bold flex items-center gap-1">
                  <Sparkles className="w-3.5 h-3.5 text-[#D06892]" /> Tips från din pet:
                </p>
                <p className="opacity-80">{selected.hint}</p>
                <button
                  onClick={next}
                  className="neo-btn w-full py-2 bg-[#B9FBC0] font-bold text-sm flex items-center justify-center gap-1"
                  data-testid="routine-next-btn"
                >
                  {step + 1 < STEPS.length ? 'Nästa fråga' : 'Visa avslutande tips'}
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="neo-card p-4 bg-white space-y-3 text-center" data-testid="routine-done">
            <p className="text-4xl">🐾</p>
            <p className="font-bold text-sm">Hoppas du hittade den! 💖</p>
            <p className="text-xs opacity-70">
              Om inte: ge dig själv 5 minuters paus, drick vatten och försök igen.
              Saker dyker oftast upp när vi slutar leta så hårt 🌸
            </p>
            <p className="text-[11px] opacity-60 italic">
              💡 Pro-tips: När du hittar den, logga var du la den så pet:en kommer ihåg nästa gång!
            </p>
            <div className="flex gap-2">
              <button onClick={restart} className="neo-btn flex-1 py-2 bg-[#A2D2FF] text-xs font-bold flex items-center justify-center gap-1" data-testid="routine-restart">
                <RotateCcw className="w-3.5 h-3.5" /> Börja om
              </button>
              <button onClick={onClose} className="neo-btn flex-1 py-2 bg-[#FFC8DD] text-xs font-bold" data-testid="routine-finish">
                Klar
              </button>
            </div>
          </div>
        )}

        <p className="text-[10px] opacity-50 italic text-center">
          ADHD-tips: Att leta i panik gör det värre — andas, gå runt långsamt, lita på rutinen
        </p>
      </div>
    </div>
  );
}
